import { Component, OnInit, OnDestroy } from '@angular/core';
import { ProjectService } from '../../services/project.service';
import { LoadingService } from '../../services/loading.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit, OnDestroy {

  public projects: any[] = [];
  public isLoading: boolean = false;

  // Filtros
  public searchTerm: string = '';
  public filterId: number | null = null;
  public selectedStatus: any = null;
  public selectedType: any = null;
  public startDate: Date | null = null;
  public endDate: Date | null = null;
  public dateType: string = 'Creation';
  public dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Inicio', value: 'Start' },
    { label: 'Fecha de Cierre', value: 'Closed' }
  ];

  // Opciones de dropdown
  public statusOptions: any[] = [
    { label: 'Creado', value: 'CREATED' },
    { label: 'Pre-Cierre', value: 'PRECLOSED' },
    { label: 'Cerrado', value: 'CLOSED' },
    { label: 'Inactivo', value: 'INACTIVE' },
    { label: 'Cancelado', value: 'CANCELED' }
  ];

  public typeOptions: any[] = [
    { label: 'Diploma', value: 1 },
    { label: 'Seminario', value: 2 },
    { label: 'Especialización', value: 3 },
    { label: 'Consultoría', value: 4 },
    { label: 'Pre-grado', value: 5 },
    { label: 'Postgrado', value: 6 },
    { label: 'Investigación', value: 7 },
    { label: 'Bienestar', value: 8 },
    { label: 'Otro', value: 9 }
  ];

  // Ordenamiento
  public sortBy: string = 'Id';
  public sortOrder: 'asc' | 'desc' = 'desc';

  // Paginación server-side
  public pageNumber: number = 1;
  public pageSize: number = 12;
  public totalRecords: number = 0;

  // Métricas (totales del backend, no de la página)
  public totalCreated: number = 0;
  public totalPreclosed: number = 0;
  public totalClosed: number = 0;

  private filterSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private _projectService: ProjectService,
    private loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.filterSubject.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.pageNumber = 1;
      this.loadProjects();
    });

    this.loadProjects();
    this.loadStatusCounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.isLoading = true;
    this.loadingService.show();

    const parameters: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      status: this.selectedStatus,
      typeProject: this.selectedType,
      startDate: this.startDate,
      endDate: this.endDate,
      dateType: this.dateType
    };

    if (this.filterId) parameters.filterId = this.filterId;

    this._projectService.getProjectsPaged(parameters).subscribe({
      next: (response) => {
        this.projects = response?.data || [];
        this.totalRecords = response?.totalRecords || 0;
        this.isLoading = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error("Se ha presentado un error", error);
        this.projects = [];
        this.totalRecords = 0;
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Carga los conteos por estado para las tarjetas de métricas (independiente del filtro actual).
   * Una sola llamada con pageSize=1 trae el totalRecords filtrado por estado.
   */
  private loadStatusCounts(): void {
    this._projectService.getProjectsPaged({ pageNumber: 1, pageSize: 1, status: 'CREATED' })
      .subscribe({ next: r => this.totalCreated = r?.totalRecords || 0 });
    this._projectService.getProjectsPaged({ pageNumber: 1, pageSize: 1, status: 'PRECLOSED' })
      .subscribe({ next: r => this.totalPreclosed = r?.totalRecords || 0 });
    this._projectService.getProjectsPaged({ pageNumber: 1, pageSize: 1, status: 'CLOSED' })
      .subscribe({ next: r => this.totalClosed = r?.totalRecords || 0 });
  }

  onFilterChange(): void {
    this.filterSubject.next();
  }

  onPageChange(event: any): void {
    this.pageNumber = event.page + 1;
    this.pageSize = event.rows;
    this.loadProjects();
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.loadProjects();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.filterId = null;
    this.selectedStatus = null;
    this.selectedType = null;
    this.startDate = null;
    this.endDate = null;
    this.dateType = 'Creation';
    this.pageNumber = 1;
    this.loadProjects();
  }

  getStatusProject(status: string): string {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return 'Creado';
      case "CLOSED":
        return 'Cerrado';
      case "PRECLOSED":
        return 'Pre-Cierre';
      case "INACTIVE":
        return 'Inactivo';
      case "CANCELED":
        return 'Cancelado';
      default:
        return status || 'Desconocido';
    }
  }

  getStatusColor(status: string): string {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800/30';
      case "CLOSED":
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-700';
      case "PRECLOSED":
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/30';
      case "INACTIVE":
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800/30';
      case "CANCELED":
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/30';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-700';
    }
  }

  getProjectTypeIcon(typeProject: number): string {
    switch (typeProject) {
      case 1: // Diploma
        return 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z';
      case 2: // Seminario
        return 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z';
      case 3: // Especialización
        return 'M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z';
      default:
        return 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';
    }
  }

  getProjectTypeName(typeProject: number): string {
    const types = {
      1: 'Diploma',
      2: 'Seminario',
      3: 'Especialización',
      4: 'Consultoría',
      5: 'Pre-grado',
      6: 'Postgrado',
      7: 'Investigación',
      8: 'Bienestar',
      9: 'Otro'
    };
    return types[typeProject as keyof typeof types] || 'Desconocido';
  }
}
